// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import FormField, {
    FormFieldClickable,
    FormFieldComponentProps,
    FormFieldIcon,
    FormFieldSizeVariant,
} from "@eisinc/MobiusOverrides/FormField";
import { sizeVariantValues } from "@eisinc/MobiusOverrides/FormField/formStyles";
import { formatPhoneNumber } from "@eisinc/XDL/Utilities/inputValidation";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import { FormFieldPresentationProps } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { HasDisablerContext, withDisabler } from "@insite/mobius/utilities/DisablerContext";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import { MobiusStyledComponentPropsWithRef } from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import { ThemeConsumer } from "styled-components";

export interface TextFieldPresentationProps extends FormFieldPresentationProps<TextFieldComponentProps> {
    /** Props to be passed into the Clickable component that will optionally wrap the icon.
     * If `clickableProps` is passed, the icon will be wrapped in a Clickable. If not, the icon will not be clickable. */
    iconClickableProps?: ClickablePresentationProps;
    /** The props for icon displaying on the text input field.
     * @themable */
    iconProps?: IconPresentationProps;
    info?: string;
    css?: StyledProp<TextFieldComponentProps>;
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** Background color of the form element */
    backgroundColor?: string;
    /** The props for the textfield label */
    labelProps?: TypographyPresentationProps;
    /** Boolean value for width animation */
    isAnimated?: boolean;
}

type TextFieldComponentProps = MobiusStyledComponentPropsWithRef<
    "input",
    { ref?: React.Ref<HTMLInputElement | null> },
    {
        /** The props for icon displaying on the text input field. */
        iconProps?: IconPresentationProps;
        /** Props to be passed into the Clickable component that will optionally wrap the icon.
         * If `clickableProps` is passed, the icon will be wrapped in a Clickable. If not, the icon will not be clickable. */
        iconClickableProps?: ClickableProps;
        /** Visually hidden text to be rendered within the text field clickable. */
        clickableText?: string;
        /** Error message to be displayed below the input. */
        error?: React.ReactNode;
        /** Hint text to be displayed below the input. */
        hint?: React.ReactNode;
        /** Label to be displayed above the input. */
        label?: React.ReactNode;
        /** XNG-Changes: add clear icon props */
        clearClickableProps?: {
            clickableProps?: ClickableProps;
            iconProps?: IconPresentationProps;
        };

        customMxValidation?: boolean;
    } & Partial<FormFieldComponentProps>
>;

export type TextFieldProps = TextFieldComponentProps & TextFieldPresentationProps;

const omitKeys = [
    "sizeVariant",
    "border",
    "label",
    "labelPosition",
    "theme",
    "cssOverrides",
    "iconProps",
    "backgroundColor",
    "labelProps",
    "dispatch",
    "labelId",
    "isAnimated",
    "aria-expanded",
    "aria-controls",
] as (keyof Omit<
    TextFieldProps,
    | "id"
    | "onChange"
    | "clickableText"
    | "disabled"
    | "error"
    | "hint"
    | "iconClickableProps"
    | "placeholder"
    | "required"
    | "disable"
    | "info"
    | "maxLength"
    | "min"
    | "max"
    | "isAnimated"
    | "clearClickableProps"
>)[];

const validNumber = (event: React.ChangeEvent<HTMLInputElement>, min?: number | string, max?: number | string) => {
    const number = Number(event.currentTarget.value);
    if (min !== undefined && number < Number(min)) {
        return false;
    }

    if (max !== undefined && number > Number(max)) {
        return false;
    }

    return true;
};

/**
 * TextField is a form element with an optional label, hint text, error message and optional icon.
 * Props not contained in the list below get passed into the input component (e.g. event handlers, `value`, etc).
 */
const TextField: React.FC<TextFieldProps & HasDisablerContext> = props => {
    return (
        <ThemeConsumer>
            {(theme?: BaseTheme) => {
                const {
                    clickableText,
                    disable,
                    disabled,
                    error,
                    hint,
                    iconClickableProps,
                    id,
                    placeholder,
                    required,
                    info,
                    maxLength,
                    onChange,
                    min,
                    max,
                    isAnimated,
                    // XNG-Changes
                    clearClickableProps,
                    ...otherProps
                } = props;
                // Because disabled html attribute doesn't accept undefined

                const isDisabled = !!(disable || disabled);
                const { applyProp, spreadProps } = applyPropBuilder(
                    { theme, ...otherProps },
                    { component: "textField", category: "formField" },
                );
                const inputId = id || uniqueId();
                const labelId = `${inputId}-label`;
                const inputLabelObj = otherProps.label === 0 || otherProps.label ? { "aria-labelledby": labelId } : {};
                const descriptionId = `${inputId}-description`;
                const hasDescription = error || hint;
                const labelProps = applyProp("labelProps");
                const sizeVariant: FormFieldSizeVariant = applyProp("sizeVariant", "default");
                const iconProps = spreadProps("iconProps");

                // <XNG-Changes>
                const clearIconProps = {
                    ...clearClickableProps?.iconProps,
                    color: isDisabled ? "common.disabled" : clearClickableProps?.iconProps?.color || "common.ui5",
                    hoverColor: "common.ui8",
                    src: clearClickableProps?.iconProps?.src || X,
                    width: clearClickableProps?.iconProps?.size || 16,
                    height: clearClickableProps?.iconProps?.size || 16,
                };

                const simulateClearHover = (otherProps as any)["data-simulate-clear-hover"];
                const clearIconElement =
                    clearClickableProps && !isDisabled ? (
                        <FormFieldClickable
                            {...clearClickableProps.clickableProps}
                            data-role="clear-clickable"
                            data-simulate-state={simulateClearHover ? "hover" : undefined}
                            disabled={isDisabled}
                        >
                            <FormFieldIcon {...clearIconProps} />
                            <VisuallyHidden>{translate("Clear")}</VisuallyHidden>
                        </FormFieldClickable>
                    ) : null;
                // </XNG-Changes>

                let formIcon = (
                    <FormFieldIcon
                        {...iconProps}
                        size={sizeVariantValues[sizeVariant].icon}
                        color={isDisabled ? "text.disabled" : iconProps.color || "text.main"}
                    />
                );
                if (iconClickableProps) {
                    formIcon = (
                        <FormFieldClickable
                            {...iconClickableProps}
                            data-role="icon-clickable"
                            disabled={isDisabled}
                            data-simulate-state={(otherProps as any)["data-simulate-icon-hover"] ? "hover" : undefined}
                        >
                            {formIcon}
                            <VisuallyHidden>{clickableText}</VisuallyHidden>
                        </FormFieldClickable>
                    );
                }

                const textInput = (
                    <>
                        <input
                            ref={props.ref}
                            type="text"
                            id={inputId}
                            aria-labelledby={labelId}
                            aria-describedby={hasDescription ? descriptionId : undefined}
                            aria-invalid={!!error}
                            aria-required={!disabled && required}
                            tabIndex={0}
                            maxLength={maxLength || 255}
                            {...{ disabled: isDisabled, placeholder, required }}
                            {...omitMultiple(otherProps, omitKeys)}
                            {...inputLabelObj}
                            onChange={(e: any) => {
                                const target = e.target;
                                const value = target.value;

                                if (required && value && !value.trim()) {
                                    return;
                                }

                                if (otherProps?.type === "tel") {
                                    e.target.value = formatPhoneNumber(value, otherProps.customMxValidation);
                                }

                                onChange && onChange(e);
                            }}
                        />
                        {/* <XNG-Changes>: add clear icon element */}
                        <StyledWrapper className="clickable-wrapper">
                            {clearIconElement && clearIconElement}
                            {iconProps ? formIcon : null}
                        </StyledWrapper>
                    </>
                );

                return (
                    <FormField
                        descriptionId={descriptionId}
                        formInput={textInput}
                        labelId={labelId}
                        inputId={inputId}
                        labelProps={labelProps}
                        {...props}
                    />
                );
            }}
        </ThemeConsumer>
    );
};

TextField.displayName = "TextField";
/** @component */
export default withDisabler(TextField);
